import React from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import pluralize from 'lib/pluralize'
import { currency } from 'lib/utils/string'
import { useAuth } from 'lib/auth'
import {
  isMonthlyRecurringPayment,
  isRecurringPayment,
  isWeeklyRecurringPayment,
} from 'utils/groupEventUtils'
import { recurringPaymentDisclaimer } from '../../components/price-details'
import useStyles from './styles'

const unitLabel = groupEvent => {
  if (isWeeklyRecurringPayment(groupEvent)) return ' / week'
  if (isMonthlyRecurringPayment(groupEvent)) return ' / month'
  return ''
}

const PriceSummary = ({ groupEvent, paymentRequired, setPaymentRequired }) => {
  const classes = useStyles()
  const { user } = useAuth()
  const { watch } = useFormContext()
  const participants = watch('participants') || []

  const { assistantCoaches, acceptsOnlinePayments } = groupEvent
  const isAssistantCoach = assistantCoaches?.some(
    coach => coach.id === user?.id,
  )
  const canSkipPayment =
    (user && user?.id === groupEvent?.coach?.id) || isAssistantCoach

  const count = participants.length
  const priceInDollars = groupEvent.priceInCents / 100
  const totalInDollars = (groupEvent.priceInCents * count) / 100
  const isRecurring = isRecurringPayment(groupEvent)
  const showSessionsNote = !isRecurring && groupEvent.lessonCount > 1

  return (
    <div>
      {canSkipPayment && setPaymentRequired && acceptsOnlinePayments && (
        <FormControlLabel
          control={
            <Switch
              checked={!paymentRequired}
              onChange={() => setPaymentRequired(!paymentRequired)}
            />
          }
          label="Skip payment"
        />
      )}
      <div className={classes.priceRow}>
        <span>
          {currency(priceInDollars)}
          {unitLabel(groupEvent)} × {count} {pluralize(count, 'participant')}
        </span>
        <span>{currency(totalInDollars)}</span>
      </div>
      {paymentRequired ? (
        <>
          <div className={classes.dueRow}>
            <span className={classes.dueLabel}>Due today</span>
            <Typography
              variant="h5"
              component="span"
              className={classes.dueAmount}
            >
              {currency(totalInDollars)}
            </Typography>
          </div>
          {groupEvent.numberOfPayments > 1 && (
            <p className={classes.priceNote}>
              {recurringPaymentDisclaimer({
                groupEvent,
                totalFeesInDollars: totalInDollars,
              })}
            </p>
          )}
        </>
      ) : (
        <>
          <div className={classes.dueRow}>
            <span className={classes.dueLabel}>Due today</span>
            <Typography
              variant="h5"
              component="span"
              className={classes.dueAmount}
            >
              $0.00
            </Typography>
          </div>
          <p className={classes.priceNote}>
            Total {currency(totalInDollars)}. Pay at location, state and local
            taxes may apply.
          </p>
        </>
      )}
      {showSessionsNote && (
        <p className={classes.priceNote}>
          One price covers all {groupEvent.lessonCount} sessions.
        </p>
      )}
    </div>
  )
}

PriceSummary.propTypes = {
  groupEvent: PropTypes.object.isRequired,
  paymentRequired: PropTypes.bool,
  setPaymentRequired: PropTypes.func,
}

export default PriceSummary
